import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Close,
  RestartAlt,
  Search,
  Tune
} from '@mui/icons-material';

const toOptions = (value) => (Array.isArray(value) ? value : []);

const LogsFilters = ({
  filters,
  defaultFilters,
  filterOptions,
  onApply,
  onClose
}) => {
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  const perPageOptions = useMemo(() => {
    return toOptions(filterOptions.per_page_options).length
      ? filterOptions.per_page_options
      : [10, 20, 50, 100, 1000, 2000, 5000];
  }, [filterOptions.per_page_options]);

  const sortOptions = useMemo(() => {
    return toOptions(filterOptions.sort_columns).length
      ? filterOptions.sort_columns
      : ['created_at', 'id', 'log_level', 'classification', 'module', 'action', 'status', 'performed_by'];
  }, [filterOptions.sort_columns]);

  const handleChange = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApply = () => {
    onApply(draft);
  };

  const handleReset = () => {
    setDraft(defaultFilters);
    onApply(defaultFilters);
  };

  const renderMultiValue = (selected, emptyLabel) => {
    if (!selected.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => (
          <Chip key={value} label={value} size="small" />
        ))}
      </Box>
    );
  };

  const selectField = (key, label, options, allLabel) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        value={draft[key] || ''}
        label={label}
        onChange={(event) => handleChange(key, event.target.value)}
      >
        <MenuItem value="">{allLabel}</MenuItem>
        {toOptions(options).map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const textField = (key, label, props = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={draft[key] || ''}
      onChange={(event) => handleChange(key, event.target.value)}
      {...props}
    />
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2
      }}
    >
      <Stack spacing={2.25}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Tune color="primary" />
            <Typography variant="h6" fontWeight={800}>
              Filters
            </Typography>
          </Stack>
          <Tooltip title="Close filters">
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {textField('search', 'Search message, record, user, request')}
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date From"
              value={draft.date_from || ''}
              onChange={(event) => handleChange('date_from', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date To"
              value={draft.date_to || ''}
              onChange={(event) => handleChange('date_to', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Rows</InputLabel>
              <Select
                value={draft.limit || 10}
                label="Rows"
                onChange={(event) => handleChange('limit', Number(event.target.value))}
              >
                {perPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {Number(option).toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort</InputLabel>
              <Select
                value={draft.sort_by || 'created_at'}
                label="Sort"
                onChange={(event) => handleChange('sort_by', event.target.value)}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option.replaceAll('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Log Levels</InputLabel>
              <Select
                multiple
                displayEmpty
                value={draft.log_level || []}
                input={<OutlinedInput label="Log Levels" />}
                onChange={(event) => handleChange('log_level', event.target.value)}
                renderValue={(selected) => renderMultiValue(selected, 'All log levels')}
              >
                {toOptions(filterOptions.log_levels).map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Classifications</InputLabel>
              <Select
                multiple
                displayEmpty
                value={draft.classification || []}
                input={<OutlinedInput label="Classifications" />}
                onChange={(event) => handleChange('classification', event.target.value)}
                renderValue={(selected) => renderMultiValue(selected, 'All classifications')}
              >
                {toOptions(filterOptions.classifications).map((classification) => (
                  <MenuItem key={classification} value={classification}>
                    {classification}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('module', 'Module', filterOptions.modules, 'All Modules')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('sub_module', 'Sub Module', filterOptions.sub_modules, 'All Sub Modules')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('action', 'Action', filterOptions.actions, 'All Actions')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('status', 'Status', filterOptions.statuses, 'All Statuses')}
          </Grid>
        </Grid>

        <Divider />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('performed_by', 'Performed By', filterOptions.performed_by, 'All Performers')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {textField('user_id', 'User ID', { type: 'number' })}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('user_role', 'User Role', filterOptions.user_roles, 'All Roles')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('system_task', 'System Task', filterOptions.system_tasks, 'All Tasks')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {textField('record_id', 'Record ID')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('record_type', 'Record Type', filterOptions.record_types, 'All Record Types')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {textField('ip_address', 'IP Address')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {textField('request_id', 'Request ID')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField('error_code', 'Error Code', filterOptions.error_codes, 'All Error Codes')}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Direction</InputLabel>
              <Select
                value={draft.sort_direction || 'desc'}
                label="Direction"
                onChange={(event) => handleChange('sort_direction', event.target.value)}
              >
                <MenuItem value="desc">Newest / Desc</MenuItem>
                <MenuItem value="asc">Oldest / Asc</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<RestartAlt />} onClick={handleReset}>
            Reset
          </Button>
          <Button variant="contained" startIcon={<Search />} onClick={handleApply}>
            Apply
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default LogsFilters;
